import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Account, BaseTransaction, Pagination, Response } from '@ui/src';
import { Fintrac, FintracTransaction, PaymentType } from '../../types';
import { fintracApi } from '../services';

export interface FintracState {
  reports?: Fintrac[];
  reportsPagination?: Pagination;
  reportsErrorsList?: string;
  transactions?: FintracTransaction[];
  transactionsPagination?: Pagination;
  selectedTransactions?: string[];
  overdraftEnabled?: boolean;
  transactionAccounts?: Account[];
  paymentTypes?: PaymentType[];
  incomingTransactions?: BaseTransaction[];
  incomingTransactionsPagination?: Pagination;
  errorsList?: string;
  report?: Fintrac;
  reportTransactions?: FintracTransaction[];
  reportTransactionsPagination?: Pagination;
  selectedReportId?: string;
}

const initialState: FintracState = {};

export const FintracSlice = createSlice({
  name: 'fintrac',
  initialState,
  reducers: {
    setSelectedTransactions: (state, action: PayloadAction<string[]>) => {
      state.selectedTransactions = action.payload;
    },
    setTransactionAccounts: (state, action: PayloadAction<Account[]>) => {
      state.transactionAccounts = action.payload;
    },
    setFintracSettings: (
      state,
      action: PayloadAction<{ key: string; data: { value: boolean } }[]>,
    ) => {
      const overdraftSetting = action.payload?.find(
        (item) => item.key === 'OVERDRAFT-ENABLED',
      );

      if (overdraftSetting) {
        if (
          overdraftSetting.data.value === undefined ||
          overdraftSetting.data.value === null
        ) {
          state.overdraftEnabled = false;
        } else {
          state.overdraftEnabled = overdraftSetting.data.value;
        }
      }
    },
    setSelectedReportId: (state, action: PayloadAction<string>) => {
      state.selectedReportId = action.payload;
    },
    clearFintracTranscationsState: (state) => {
      state.incomingTransactions = undefined;
      state.incomingTransactionsPagination = undefined;
      state.errorsList = undefined;
    },
    clearReports: (state) => {
      state.reports = undefined;
      state.reportsPagination = undefined;
      state.selectedReportId = undefined;
      state.reportsErrorsList = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      fintracApi.endpoints.getFintracPaymentTypes.matchFulfilled,
      (state, action: PayloadAction<Response<PaymentType>>) => {
        state.paymentTypes = action.payload.data?.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      fintracApi.endpoints.getFintracReports.matchPending,
      (state) => {
        state.reportsErrorsList = undefined;
      },
    );
    builder.addMatcher(
      fintracApi.endpoints.getFintracReports.matchRejected,
      (state, action: any) => {
        state.reports = undefined;
        state.reportsPagination = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.reportsErrorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.reportsErrorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      fintracApi.endpoints.getFintracReports.matchFulfilled,
      (state, action: PayloadAction<Response<Fintrac>>) => {
        state.reports = action.payload.data.edges?.map((item) => ({
          ...item.node,
          deadlineAt: new Date(
            new Date(item.node.periodEnd).setDate(
              new Date(item.node.periodEnd).getDate() + 4,
            ),
          ).toISOString(),
          values: [],
        }));
        state.reportsPagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      fintracApi.endpoints.selectAllTransactionInReport.matchFulfilled,
      (state) => {
        const nextTransactions = state.transactions?.map((item) => {
          const result = { ...item };

          result.isSelectedForReport = true;

          return result;
        });

        state.selectedTransactions = nextTransactions?.map((item) => item.id);
        state.transactions = nextTransactions;
      },
    );
    builder.addMatcher(
      fintracApi.endpoints.deselectAllTransactionInReport.matchFulfilled,
      (state) => {
        state.transactions = state.transactions?.map((item) => {
          const result = { ...item };

          result.isSelectedForReport = false;

          return result;
        });

        state.reports = state.reports?.map((item) => {
          const result = { ...item };

          if (result.id === state.selectedReportId) {
            result.totalAmount = 0;
          }

          return result;
        });

        state.selectedTransactions = [];
      },
    );
    builder.addMatcher(
      fintracApi.endpoints.getFintracReportTransactions.matchFulfilled,
      (state, action: PayloadAction<Response<FintracTransaction>>) => {
        state.transactions = action.payload.data.edges?.map(
          (item) => item.node,
        );
        state.transactionsPagination = action.payload.data.pageInfo;

        state.selectedTransactions = action.payload.data.edges
          .filter((item) => item.node.isSelectedForReport)
          ?.map((item) => item.node.id);
      },
    );
    builder.addMatcher(
      fintracApi.endpoints.getFintracReport.matchFulfilled,
      (state, action: PayloadAction<{ data: Fintrac }>) => {
        state.reports = state.reports?.map((item) => {
          const result = { ...item };

          if (result.id === action.payload.data.id) {
            result.totalAmount = action.payload.data.totalAmount;
            result.status = action.payload.data.status;
          }

          return result;
        });
      },
    );
    builder.addMatcher(
      fintracApi.endpoints.submitReport.matchFulfilled,
      (
        state,
        action: {
          meta: {
            arg: {
              originalArgs: string;
            };
          };
        },
      ) => {
        state.reports = state.reports?.map((item) => {
          const result = { ...item };

          if (result.id === action.meta.arg.originalArgs) {
            result.status = 'SENT';
          }

          return result;
        });
      },
    );
    builder.addMatcher(
      fintracApi.endpoints.cancelReport.matchFulfilled,
      (
        state,
        action: {
          meta: {
            arg: {
              originalArgs: string;
            };
          };
        },
      ) => {
        state.reports = state.reports?.map((item) => {
          const result = { ...item };

          if (result.id === action.meta.arg.originalArgs) {
            result.status = 'CANCELLED';
          }

          return result;
        });
      },
    );
    builder.addMatcher(
      fintracApi.endpoints.selectTransactionInReport.matchFulfilled,
      (state, action: PayloadAction<{ data: FintracTransaction }>) => {
        state.transactions = state?.transactions?.map((item) => {
          const result = { ...item };

          if (item.id === action.payload.data.id) {
            result.isSelectedForReport =
              action.payload.data.isSelectedForReport;
          }

          return result;
        });

        let nextSelectedTransactions: string[] = [];

        if (state.selectedTransactions) {
          nextSelectedTransactions = ([] as string[]).concat(
            state.selectedTransactions,
          );
        }

        if (action.payload.data.isSelectedForReport) {
          nextSelectedTransactions.push(action.payload.data.id);
        } else {
          nextSelectedTransactions = nextSelectedTransactions?.filter(
            (id) => id !== action.payload.data.id,
          );
        }

        state.reports = state.reports?.map((item) => {
          const result = { ...item };

          if (result.id === state.selectedReportId) {
            let amount = result.totalAmount;

            if (action.payload.data.isSelectedForReport) {
              amount += action.payload.data.convertedAmount;
            } else {
              amount -= action.payload.data.convertedAmount;
            }

            result.totalAmount = amount;
          }

          return result;
        });

        state.selectedTransactions = nextSelectedTransactions;
      },
    );
    builder.addMatcher(
      fintracApi.endpoints.getFintracIncomingTransactions.matchPending,
      (state) => {
        state.errorsList = undefined;
      },
    );
    builder.addMatcher(
      fintracApi.endpoints.getFintracIncomingTransactions.matchRejected,
      (state, action: any) => {
        state.incomingTransactions = undefined;
        state.incomingTransactionsPagination = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.errorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.errorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      fintracApi.endpoints.getFintracIncomingTransactions.matchFulfilled,
      (state, action: PayloadAction<Response<BaseTransaction>>) => {
        state.incomingTransactions = action.payload.data?.edges?.map(
          (item) => ({
            ...item.node,
            url: `/transactions/incoming/${item.node.id}`,
          }),
        );
        state.incomingTransactionsPagination = action.payload.data.pageInfo;
      },
    );
  },
});

export const {
  setFintracSettings,
  clearFintracTranscationsState,
  clearReports,
  setSelectedTransactions,
  setTransactionAccounts,
  setSelectedReportId,
} = FintracSlice.actions;

export default FintracSlice.reducer;
